// Sign in, create an account, reset a password.
//
// ⚠ WHY THIS REPLACED THE MAGIC LINK. The app used to sign people in with an emailed link and no password at
// all. That is fewer moving parts and it is genuinely worse to live with: every single sign-in needs an inbox,
// on the device you are sitting at, inside the link's lifetime — so signing in on the table laptop means
// finding your phone, and a mail client that helpfully "previews" links can burn a one-time token before you
// ever click it. A password is the thing people expect, and it works on a machine with no mail on it.
//
// ⚠⚠ EMAIL IS STILL IN THE FLOW ONCE, AND ONLY ONCE, PER ACCOUNT. You cannot let somebody choose a password
// for an address they have not proved they own. So creating an account emails a link, that link is where the
// password gets chosen, and after that the address and password are all it takes forever. Resetting works the
// same way, because it is the same problem: prove the address, then choose.
//
// The shape, exactly as specified:
//
//   sign in ──── email + password ────────────────────────────────────────► in
//      │
//      ├─ create account ─► email ─► [link] ─► /welcome ─► set password ─► back to sign in, "password set"
//      └─ reset password ─► email ─► [link] ─► /reset   ─► set password ─► back to sign in, "password reset"
//
// ⚠ THE TWO LANDING PATHS ARE SEPARATE ON PURPOSE. Both do the same thing to the account, but they are not the
// same event to the person: one is finishing a sign-up and the other is recovering an account, and they need
// different words. Reading which is which from the URL PATH is reliable; reading it from Supabase's `type=`
// hash parameter is not, because that parameter is consumed and cleared by the client library before this
// component ever renders.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { SplashTitle } from "./splash-title";
import {
  friendlyAuthError, PASSWORD_RULE, passwordProblem, RESET_PATH, TOAST_KEY, WELCOME_PATH,
} from "../lib/auth-rules";

/** Read and clear the one-line confirmation left behind by a completed password change. */
function takeNotice(): string {
  try {
    const n = sessionStorage.getItem(TOAST_KEY);
    if (n) sessionStorage.removeItem(TOAST_KEY);
    return n ?? "";
  } catch {
    // Private browsing can refuse sessionStorage entirely. Losing the confirmation is a cosmetic loss; a
    // sign-in screen that throws instead of rendering is not.
    return "";
  }
}

function leaveNotice(message: string): void {
  try { sessionStorage.setItem(TOAST_KEY, message); } catch { /* see takeNotice */ }
}

type Mode = "signin" | "create" | "forgot";

/**
 * @param intro  Replaces the app's own title and blurb — the invite page says what you are joining instead.
 * @param playing  An invite is waiting: the "check your email" page warns about opening the link in another browser.
 */
export function SignIn({ intro, playing }: { intro?: React.ReactNode; playing?: boolean } = {}) {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [notice, setNotice] = useState(takeNotice);

  const go = useCallback((next: Mode) => {
    setMode(next);
    setError("");
    setSentTo("");
    setPassword("");
  }, []);

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    const address = email.trim();
    const origin = window.location.origin;

    // ⚠ Supabase RETURNS errors rather than throwing them, so every one of these reads `error` explicitly. A
    // try/catch here would look protective and catch nothing. See HANDOFF.md.
    if (mode === "signin") {
      const { error: err } = await supabase.auth.signInWithPassword({ email: address, password });
      setBusy(false);
      if (err) setError(friendlyAuthError(err.message));
      // No success branch: the session arrives through onAuthStateChange and the app swaps itself out.
      return;
    }

    if (mode === "create") {
      /**
       * ⚠ A RANDOM PASSWORD, DISCARDED IMMEDIATELY. `signUp` demands one, and asking for a password here would
       * mean typing it before the address is proved — so the account is created with a value nobody has ever
       * seen and the real password is chosen on `/welcome`, behind the emailed link. Until then the account is
       * unconfirmed and cannot sign in at all.
       */
      const throwaway = crypto.randomUUID() + crypto.randomUUID();
      const { data, error: err } = await supabase.auth.signUp({
        email: address,
        password: throwaway,
        options: { emailRedirectTo: `${origin}${WELCOME_PATH}` },
      });
      setBusy(false);
      if (err) { setError(friendlyAuthError(err.message)); return; }
      /**
       * ⚠ THE SILENT DUPLICATE. For an address that already has a confirmed account Supabase returns SUCCESS
       * with a decoy user and sends no email — deliberate, so a stranger cannot use this form to discover who
       * has an account. The tell is an empty `identities` array. Without this check the honest owner of that
       * address is told to check their inbox and nothing ever arrives, which is the worst outcome of the three.
       */
      if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        setError("That address already has an account. Sign in below, or reset the password.");
        return;
      }
      setSentTo(address);
      return;
    }

    const { error: err } = await supabase.auth.resetPasswordForEmail(address, {
      redirectTo: `${origin}${RESET_PATH}`,
    });
    setBusy(false);
    if (err) { setError(friendlyAuthError(err.message)); return; }
    // ⚠ Deliberately the same message whether or not the address exists — the reset endpoint reports success
    // either way, and inventing a "no such account" here would leak exactly what Supabase is hiding.
    setSentTo(address);
  }, [mode, email, password]);

  if (sentTo) {
    const creating = mode === "create";
    return (
      <main className="pane">
        <h1>Check your email.</h1>
        <p>
          {creating
            ? <>A link to finish setting up your account is on its way to <strong>{sentTo}</strong>. Open it and
                choose a password.</>
            : <>A link to reset your password is on its way to <strong>{sentTo}</strong>. Open it and choose a
                new one.</>}
        </p>
        {playing && (
          <p className="notice">
            <strong>Open the link in this browser.</strong> If your email opens links somewhere else, copy the link and
            paste it into this browser&rsquo;s address bar — that is where your invite is waiting.
          </p>
        )}
        <p className="muted tiny">
          The link works for one hour. If it isn&rsquo;t there in a minute or two, check the spam folder — then
          come back and ask for another.
        </p>
        <button className="link" onClick={() => go("signin")}>Back to sign in</button>
      </main>
    );
  }

  return (
    <main className="pane">
      {intro ?? (
        <SplashTitle
          sizes="(max-width: 700px) 100vw, 34rem"   /* the sign-in column is .pane, 34rem */
          sub="Build battle maps, composites and handouts — and pull in free tokens and sounds that arrive already licensed and credited."
        />
      )}

      {notice && <p className="notice" role="status">{notice}</p>}

      <form onSubmit={submit}>
        <label htmlFor="email">Email address</label>
        <input
          id="email" type="email" required autoComplete="email" placeholder="you@example.com"
          value={email} onChange={(e) => setEmail(e.target.value)}
        />

        {mode === "signin" && (
          <>
            <label htmlFor="password">Password</label>
            <input
              id="password" type={show ? "text" : "password"} required autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
            <label className="check">
              <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
              Show password
            </label>
          </>
        )}

        {mode === "create" && (
          <p className="muted tiny">
            We&rsquo;ll email you a link. You choose your password on the page it opens — that way nobody can
            make an account with an address that isn&rsquo;t theirs.
          </p>
        )}
        {mode === "forgot" && (
          <p className="muted tiny">
            We&rsquo;ll email you a link that lets you choose a new password.
          </p>
        )}

        <button type="submit" className="primary"
                disabled={busy || !email.trim() || (mode === "signin" && !password)}>
          {busy
            ? "Working…"
            : mode === "signin" ? "Sign in"
            : mode === "create" ? "Email me a sign-up link"
            : "Email me a reset link"}
        </button>
      </form>

      {error && <p className="error" role="alert">{error}</p>}

      <nav className="authswitch">
        {mode !== "signin" && (
          <button className="link" onClick={() => go("signin")}>← Back to sign in</button>
        )}
        {mode !== "create" && (
          <button className="link" onClick={() => go("create")}>Create an account</button>
        )}
        {mode !== "forgot" && (
          <button className="link" onClick={() => go("forgot")}>Forgot your password?</button>
        )}
      </nav>
    </main>
  );
}

/**
 * The page the emailed link opens: choose a password.
 *
 * ⚠ IT HAS TO WAIT FOR A SESSION, AND THE WAIT IS THE WHOLE TRICK. The link lands here with credentials in the
 * URL fragment, which the Supabase client reads and exchanges asynchronously; ask for the session in the first
 * render and it is not there yet, and this page would tell every single arrival that their link had expired.
 * So it listens as well as asks, and only calls the link dead once the client has finished looking.
 */
export function SetPassword({ mode }: { mode: "welcome" | "reset" }) {
  const [ready, setReady] = useState<"waiting" | "ok" | "nolink">("waiting");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let settled = false;
    const accept = () => { if (!settled) { settled = true; setReady("ok"); } };

    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => { if (session) accept(); });
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) { accept(); return; }
      /**
       * ⚠ NOT AN IMMEDIATE FAILURE. `getSession` can resolve before the fragment has been processed, so a
       * short grace period is the difference between "choose your password" and a false "this link is dead"
       * shown to somebody holding a perfectly good link.
       */
      setTimeout(() => { if (!settled) { settled = true; setReady("nolink"); } }, 2500);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const problem = passwordProblem(password, confirm);

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordProblem(password, confirm)) return;
    setError("");
    setBusy(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) { setBusy(false); setError(friendlyAuthError(err.message)); return; }
    /**
     * ⚠ SIGNED OUT ON PURPOSE, then sent back to the front door to sign in with what they just chose. The link
     * already granted a session, so we could simply drop them into the app — but then the first time they use
     * their new password is days later, from memory, with no idea whether it was saved. Using it once, now, is
     * what turns "I set a password" into "I know my password works".
     */
    await supabase.auth.signOut();
    leaveNotice(mode === "welcome"
      ? "Your password is set. Sign in with it below."
      : "Your password has been reset. Sign in with it below.");
    // A real navigation rather than a state change: it guarantees no fragment, no stale session and no
    // half-torn-down listener survives into the sign-in screen.
    window.location.assign("/");
  }, [password, confirm, mode]);

  if (ready === "waiting") {
    return <main className="pane"><p className="muted">Checking your link…</p></main>;
  }

  if (ready === "nolink") {
    return (
      <main className="pane">
        <h1>That link has expired.</h1>
        <p>
          Sign-up and reset links last one hour and can only be used once. Ask for a fresh one and it will work
          straight away.
        </p>
        <button className="primary" onClick={() => window.location.assign("/")}>Back to sign in</button>
      </main>
    );
  }

  return (
    <main className="pane">
      <h1>{mode === "welcome" ? "Choose a password" : "Choose a new password"}</h1>
      <p className="muted">
        {mode === "welcome"
          ? "Your email address is confirmed. Set a password and you're done — that's all you'll need from now on."
          : "Set a new password for your account."}
      </p>

      <form onSubmit={submit}>
        <label htmlFor="pw">Password</label>
        <input
          id="pw" type={show ? "text" : "password"} required autoComplete="new-password" autoFocus
          value={password} onChange={(e) => setPassword(e.target.value)}
        />

        <label htmlFor="pw2">Confirm password</label>
        <input
          id="pw2" type={show ? "text" : "password"} required autoComplete="new-password"
          value={confirm} onChange={(e) => setConfirm(e.target.value)}
        />

        <label className="check">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
          Show passwords
        </label>

        <p className="muted tiny">{PASSWORD_RULE[0]!.toUpperCase() + PASSWORD_RULE.slice(1)}. A short phrase with a number in it works well.</p>

        <button type="submit" className="primary" disabled={busy || !!problem}>
          {busy ? "Saving…" : mode === "welcome" ? "Set my password" : "Reset my password"}
        </button>
      </form>

      {/* Only nags once there is something to nag about — an untouched form is not a mistake. */}
      {password && problem && <p className="muted tiny" role="status">{problem}</p>}
      {error && <p className="error" role="alert">{error}</p>}
    </main>
  );
}
